import { hasPermission, PERMISSIONS, type Permission, systemRoleLabel } from '@repo/contracts';
import { type NavItem, AppShell as SharedAppShell } from '@repo/ui';
import { Blocks, Users } from 'lucide-react';
import { Outlet } from 'react-router';

import { APP_ROUTES } from '@/constants/app-routes';
import { activeAppRoleOf } from '@/hooks/auth/app-roles';
import { useSignOut } from '@/hooks/auth/use-sign-out';
import { SwitchRoleMenu } from '@/shared/components/switch-role/SwitchRoleMenu';
import {
  selectActiveRole,
  selectCurrentUser,
  selectHeldRoles,
  selectPermissions,
} from '@/store/auth.slice';
import { useAppSelector } from '@/store/store';

import { MobileNav } from './MobileNav';
import { MyAccessMenuItem } from './MyAccessMenuItem';

const NAV_ITEMS: NavItem[] = [
  {
    to: APP_ROUTES.people,
    label: 'People',
    icon: Users,
    permission: PERMISSIONS.userRead,
  },
  {
    to: APP_ROUTES.applications,
    label: 'Applications & roles',
    icon: Blocks,
    permission: PERMISSIONS.roleRead,
  },
];

export function AppShell() {
  const user = useAppSelector(selectCurrentUser);
  const effectivePermissions = useAppSelector(selectPermissions);
  const activeRole = useAppSelector(selectActiveRole);
  const heldRoles = useAppSelector(selectHeldRoles);
  const signOut = useSignOut();
  const subtitleRole = activeAppRoleOf(heldRoles, activeRole);

  const canAccess = (permission?: string): boolean =>
    !permission || hasPermission(effectivePermissions, [permission as Permission]);

  return (
    <SharedAppShell
      productName="Cyrino"
      sectionName="Platform"
      navItems={NAV_ITEMS}
      canAccess={canAccess}
      fullName={user?.fullName}
      subtitle={subtitleRole ? systemRoleLabel(subtitleRole) : undefined}
      onSignOut={signOut}
      accountMenuExtra={
        <>
          <MyAccessMenuItem />
          <SwitchRoleMenu />
        </>
      }
    >
      <MobileNav onSignOut={signOut} />
      <Outlet />
    </SharedAppShell>
  );
}
